import { useTranslation } from 'react-i18next';
import { usePlan } from '../../contexts/PlanContext';
import { api } from '../../services/api';
import { Button } from '../../components/ui/button';
import { ArrowUpCircle, Check, X } from 'lucide-react';

const PlanCard = ({ name, price, current, popular, features, onUpgrade }: {
  name: string;
  price: string;
  current: boolean;
  popular?: boolean;
  features: { label: string; included: boolean }[];
  onUpgrade: () => void;
}) => {
  const { t } = useTranslation('billing');

  return (
    <div className={`rounded-lg border-2 p-6 flex flex-col relative ${current ? 'border-primary bg-primary/5' : popular ? 'border-amber-400' : 'border-slate-200'}`}>
      {popular && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-400 text-amber-900 text-xs font-semibold px-3 py-0.5 rounded-full">
          {t('most_popular')}
        </span>
      )}
      <div className="mb-4">
        <h3 className="text-lg font-semibold">{name}</h3>
        <div className="mt-1">
          <span className="text-3xl font-bold">${price}</span>
          {parseFloat(price) > 0 && <span className="text-slate-500">{t('per_month')}</span>}
        </div>
      </div>
      <ul className="space-y-2 flex-1 mb-6">
        {features.map((f, i) => (
          <li key={i} className="flex items-center gap-2 text-sm">
            {f.included
              ? <Check className="h-4 w-4 text-green-600 shrink-0" />
              : <X className="h-4 w-4 text-slate-300 shrink-0" />}
            <span className={f.included ? 'text-slate-700' : 'text-slate-400'}>{f.label}</span>
          </li>
        ))}
      </ul>
      {current ? (
        <Button variant="outline" disabled className="w-full">{t('current_plan')}</Button>
      ) : parseFloat(price) > 0 ? (
        <Button className="w-full" onClick={onUpgrade}>
          <ArrowUpCircle className="h-4 w-4 mr-1.5" />
          {t('upgrade_to', { plan: name })}
        </Button>
      ) : null}
    </div>
  );
};

const Billing = () => {
  const { t } = useTranslation('billing');
  const { plan, loading } = usePlan();

  const handleUpgrade = async (planSlug: string) => {
    try {
      const response = await api.post<{ url: string }>('/billing/checkout', { plan_slug: planSlug });
      const url = (response as any).url || (response as any).data?.url;
      if (url) window.location.href = url;
    } catch (err) {
      console.error('Checkout error:', err);
    }
  };

  const handleManage = async () => {
    try {
      const response = await api.post<{ url: string }>('/billing/portal', {});
      const url = (response as any).url || (response as any).data?.url;
      if (url) window.location.href = url;
    } catch (err) {
      console.error('Portal error:', err);
    }
  };

  const currentSlug = plan?.plan?.slug || 'free';

  const plans = [
    {
      slug: 'free',
      name: t('free_plan'),
      price: '0',
      features: [
        { label: t('unlimited_documents'), included: true },
        { label: `3 ${t('templates_used', { used: '', limit: '' }).replace(' of ', '').trim() || 'Templates'}`, included: true },
        { label: t('retention_days', { days: 90 }), included: true },
        { label: t('email_notifications'), included: true },
        { label: t('detailed_audit_logs'), included: true },
        { label: t('sms_notifications'), included: false },
        { label: t('custom_branding'), included: false },
        { label: t('team_invites'), included: false },
      ],
    },
    {
      slug: 'starter',
      name: t('starter_plan'),
      price: '8.00',
      features: [
        { label: t('everything_in_free'), included: true },
        { label: t('unlimited_retention'), included: true },
        { label: `${t('sms_notifications')} (${t('sms_us_ca')})`, included: true },
        { label: t('open_notifications'), included: true },
        { label: t('custom_branding'), included: true },
        { label: t('team_invites'), included: false },
        { label: t('shared_resources'), included: false },
      ],
    },
    {
      slug: 'team',
      name: t('team_plan'),
      price: '29.99',
      features: [
        { label: t('everything_in_starter'), included: true },
        { label: `${t('sms_notifications')} (${t('sms_global')})`, included: true },
        { label: t('team_invites'), included: true },
        { label: t('shared_resources'), included: true },
        { label: t('centralized_billing'), included: true },
        { label: t('up_to_members', { count: 10 }), included: true },
      ],
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-primary rounded-full border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Usage Summary */}
      {plan && (
        <div className="rounded-lg border p-4 bg-white">
          <h3 className="font-semibold mb-3">{t('usage')}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-slate-500">{t('templates_used', { used: '', limit: '' }).includes('of') ? 'Templates' : t('templates_unlimited')}</span>
              <div className="font-medium mt-1">
                {plan.limits.templates
                  ? t('templates_used', { used: plan.usage.templates, limit: plan.limits.templates })
                  : t('templates_unlimited')}
              </div>
            </div>
            <div>
              <span className="text-slate-500">{t('document_retention')}</span>
              <div className="font-medium mt-1">
                {plan.limits.document_retention_days
                  ? t('retention_days', { days: plan.limits.document_retention_days })
                  : t('retention_unlimited')}
              </div>
            </div>
            <div>
              <span className="text-slate-500">{t('team_members')}</span>
              <div className="font-medium mt-1">
                {plan.limits.team_members && plan.limits.team_members > 1
                  ? t('team_members_used', { used: plan.usage.team_members, limit: plan.limits.team_members })
                  : t('team_members_single')}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Plan Cards */}
      <div>
        <h3 className="font-semibold mb-3">{t('plan_comparison')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {plans.map((p) => (
            <PlanCard
              key={p.slug}
              name={p.name}
              price={p.price}
              current={currentSlug === p.slug}
              popular={p.slug === 'starter'}
              features={p.features}
              onUpgrade={() => handleUpgrade(p.slug)}
            />
          ))}
        </div>
      </div>

      {/* Manage Subscription */}
      {currentSlug !== 'free' && (
        <div className="rounded-lg border p-4 bg-white">
          <Button variant="outline" onClick={handleManage}>
            {t('manage_subscription')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default Billing;
